import React from 'react';

/**
 * Loading states for the MMEC chrome.
 *
 * Two shapes:
 *  - <ViewLoading />  — inside a shell, while a lazy tool chunk arrives.
 *  - <AppSplash />    — before the shell exists, while auth resolves.
 *
 * Both wait ~180ms before showing anything. A view that lands inside that
 * window never flashes a spinner, which is most of them on a warm cache.
 */

const css = `
  .mmec-loading {
    flex: 1;
    min-height: 160px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    color: var(--muted);
    font: 500 12px/1 var(--font-ui);
    letter-spacing: 0.02em;
    animation: mmec-fade-in var(--dur-panel) var(--ease) 180ms both;
  }
  .mmec-loading-ring {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2px solid var(--hair);
    border-top-color: var(--acc);
    animation: mmec-spin 0.9s linear infinite;
  }
  @keyframes mmec-spin { to { transform: rotate(360deg); } }

  .mmec-splash {
    position: fixed;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 18px;
    background: var(--void);
    color: var(--txt);
    font-family: var(--font-ui);
    animation: mmec-fade-in var(--dur-panel) var(--ease) 180ms both;
  }
  .mmec-splash-mark {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: var(--acc);
    color: var(--void);
    display: grid;
    place-items: center;
    font: 700 20px/1 var(--font-mono);
    box-shadow: var(--lift-2), 0 0 0 1px color-mix(in srgb, var(--acc) 30%, transparent);
  }
  .mmec-splash-word {
    font: 600 14px/1 var(--font-ui);
    letter-spacing: 0.18em;
    text-transform: uppercase;
  }
  .mmec-splash-line {
    position: relative;
    width: 160px;
    height: 2px;
    border-radius: 999px;
    background: var(--hair);
    overflow: hidden;
    margin-top: 6px;
  }
  .mmec-splash-line::after {
    content: "";
    position: absolute;
    inset: 0;
    width: 40%;
    border-radius: 999px;
    background: var(--acc);
    animation: mmec-splash-sweep 1.4s var(--ease-inout) infinite;
  }
  @keyframes mmec-splash-sweep {
    from { transform: translateX(-100%); }
    to   { transform: translateX(260%); }
  }
  .mmec-splash-caption {
    color: var(--muted);
    font-size: 12px;
  }
`;

export const ViewLoading: React.FC<{ label?: string }> = ({ label = 'Loading' }) => (
  <>
    <style>{css}</style>
    <div className="mmec-loading" role="status" aria-live="polite">
      <div className="mmec-loading-ring" aria-hidden />
      <span>{label}…</span>
    </div>
  </>
);

export const AppSplash: React.FC<{ caption?: string }> = ({ caption = 'Preparing your desk' }) => (
  <>
    <style>{css}</style>
    <div className="mmec-splash" role="status" aria-live="polite">
      <div className="mmec-splash-mark" aria-hidden>M</div>
      <div className="mmec-splash-word">MMEC</div>
      <div className="mmec-splash-line" aria-hidden />
      <div className="mmec-splash-caption">{caption}</div>
    </div>
  </>
);

export default ViewLoading;
